import { vec2 } from "@tlaukkan/tsm";
import { off } from "process";
import { Transform } from "../../shared/components";
import { CompositionElement } from "../../shared/composition";
import { CameraZoomLevel, Resolution } from "../../shared/constants";
import { getEntityById } from "../../shared/entityFactory";
import { GameElement } from "../../shared/gameElementTypes";
import { getSystem, metersToPixels } from "../../shared/utils";
import { GameClient } from "./gameClient";
import { setBugReportButtonFlashing } from "./bugReport/bugReporter";
import { CameraSystem } from "./systems/cameraSystem";
import { createTutorialGraph } from "./tutorialDefinitions";
import { IngameTutorialStep, IngameTutorialData, TutorialGraph } from "./tutorialGraph";


export class TutorialStateMachine {

    currentStep: IngameTutorialStep | null = null;
    spotlightPos: [number, number] = [0, 0];

    disableCellClicks = false;
    forceSellButtonShown = false;
    hideSellButton = false;
    forceCellDescriptionShown = false;

    onTutorialEndpoint: () => void = () => { };
    gameClient: GameClient;
    tutorialGraph: TutorialGraph;

    combineTutorialCells: CompositionElement[] = [];

    constructor(gameClient: GameClient) {
        setBugReportButtonFlashing(false);
        this.gameClient = gameClient;
        this.gameClient.tutorialsThisGame.clear();
        this.tutorialGraph = createTutorialGraph(gameClient);
    }

    update(delta: number, time: number) {
        if (this.tutorialGraph.active === null || !this.tutorialGraph.active.hasDynamicPosition) {
            return;
        }

        //Watch for position changes from the game, and update the UI
        const position = this.getPosition();
        if (this.spotlightPos[0] !== position[0]
            || this.spotlightPos[1] !== position[1]) {
            this.spotlightPos = [position[0], position[1]];
            this.gameClient.updateUI();
        }
    }

    nextStep(wasClick = false) {
        if (this.tutorialGraph.active !== null) {
            if (this.tutorialGraph.active.requiresClick && !wasClick) {
                //If the current step requires a click, we don't advance
                return;
            }
            //Move to next state
            this.tutorialGraph.active = this.tutorialGraph.active.nextState;
            const isEndPoint = this.tutorialGraph.active === null;
            this.currentStep = isEndPoint ? null : this.tutorialGraph.active!.step;
            this.gameClient.updateUI();
        }
        else {
            //reached endpoint
        }
    }

    doStep() {
        getSystem(this.gameClient.world, CameraSystem).setCameraMoveNotifications(this.onMoveCamera); //put this somewhere it calls once



        this.disableCellClicks = this.tutorialGraph.active?.options?.disableCellClicks || false;
        this.gameClient.enabledButtons = this.tutorialGraph.active?.options?.enabledButtons || undefined;
        this.forceSellButtonShown = this.tutorialGraph.active?.options?.forceSellButtonShown || false;
        this.hideSellButton = this.tutorialGraph.active?.options?.hideSellButton || false;
        this.forceCellDescriptionShown = this.tutorialGraph.active?.options?.forceCellDescriptionShown || false;

        if (setBugReportButtonFlashing) {
            setBugReportButtonFlashing(this.tutorialGraph.active?.options?.flashBugReportButton || false);
        }

        console.log('forceCellDescriptionShown', this.forceCellDescriptionShown);

        if (this.tutorialGraph.active === null) {
            this.onTutorialEndpoint();
            return;
        }

        //Setup camera focus
        const focus = this.tutorialGraph.active.options?.cameraFocus || "none";
        //Do Camera focus here?
        switch (focus) {
            case "player":
                this.gameClient.cameraSystem.focusOnPlayer(this.gameClient.getPlayer()!);
                break;
            case "enemy":
                const viruses = this.gameClient.getPlayer()?.enemyCells[0].position!;
                this.gameClient.cameraSystem.focusOnPosition(new vec2(viruses));
                break;
            case "infected":
                const infectedCells = this.gameClient.getPlayer()?.enemyCells.filter(
                    c => c.type === GameElement.INFECTED_BLOODCELL && c.entityParameters && c.entityParameters.spawnWave && c.entityParameters.spawnWave > 0);
                if (infectedCells && infectedCells.length > 0) {
                    //Find midpoint of all infected cells in the new wave
                    const midX = infectedCells.reduce((sum, cell) => sum + cell.position[0], 0) / infectedCells.length;
                    const midY = infectedCells.reduce((sum, cell) => sum + cell.position[1], 0) / infectedCells.length;
                    this.gameClient.cameraSystem.focusOnPosition(new vec2([midX, midY]));
                    this.gameClient.cameraSystem.setZoomLevel(CameraZoomLevel.Medium);
                    break;
                }
            case "bot":
                const bot = this.gameClient.players.find(player => player.isBot === true);
                if (bot) {
                    this.gameClient.cameraSystem.focusOnPlayer(bot);
                }
                break;
            case "middle":
                this.gameClient.cameraSystem.focusOnPosition(new vec2([0, 0]));
                break;
            case "none":
                break;
        }
    }

    doEvent(event: string) {
        const level = this.gameClient.trainingLevel;
        const round = this.gameClient.round;
        const changed = this.tutorialGraph.event(event, level, round);
        if (changed) {
            const isEndPoint = this.tutorialGraph.active === null;
            this.currentStep = isEndPoint ? null : this.tutorialGraph.active!.step;
            console.log('Tutorial event', event, level, round, this.currentStep);
            this.doStep();
            this.gameClient.updateUI();
        }
    }

    async waitForTutorialEndpoint() {
        if (this.tutorialGraph.active === null) return;
        return new Promise<void>((resolve, _) => {
            this.onTutorialEndpoint = () => {
                this.onTutorialEndpoint = () => { };
                resolve();
            };
        });
    }

    getStep(): IngameTutorialData | undefined {
        if (this.tutorialGraph.active !== null) {
            const [x, y, radius, vOffset] = this.getPosition();
            //console.log('Tutorial step', this.tutorialGraph.active.step, x, y, radius, vOffset)
            // console.log('Ignore click', graph.active.requiresClick(graph.active.step))

            return {
                x,
                y,
                text: this.tutorialGraph.active.text,
                radius,
                vOffset,
                requireClick: this.tutorialGraph.active.requiresClick
            }
        }
    }

    isVisible() {
        return this.tutorialGraph.active !== null;
    }

    getTutPosition(step: IngameTutorialStep) {
        return this.tutorialGraph.active?.getPosition(this.gameClient) || [0, 0];
    }

    getPosition(): [number, number, number, number] {
        if (this.tutorialGraph.active !== null) {
            const position = this.tutorialGraph.active.getPosition(this.gameClient);
            let offset = this.tutorialGraph.active.offset === null ? -position[1] + 20 : this.tutorialGraph.active.offset;
            if (offset < -42) {
                offset = 15;
            }
            return [position[0], position[1], this.tutorialGraph.active.radius, offset];
        }
        return [0, 0, 0, 0];
    }

    getSpotlightCellPosition(cellId: number): [number, number] {
        const entity = getEntityById(cellId); //comp position seems to update late
        const positionOfEntity = entity?.getComponent(Transform).position!;
        return this.getSpotlightPosition(positionOfEntity);
    }

    getSpotlightPosition(position: vec2): [number, number] {
        // Convert world position to pixel coordinates
        const scale = this.gameClient.renderEngine.sceneRoot.scale;
        const Xoffset = this.gameClient.renderEngine.sceneRoot.x;
        const Yoffset = this.gameClient.renderEngine.sceneRoot.y;
        const cameraOffsetScaleAdjX = Resolution.Width * ((1 - scale.x) / 2);
        const cameraOffsetScaleAdjY = Resolution.Height * ((1 - scale.y) / 2);
        //Note: Are we missing this cameraOffset adj in other places??
        const worldPosInPixels = new vec2([
            metersToPixels((position.x) * scale.x) + (Xoffset) - cameraOffsetScaleAdjX,
            metersToPixels((position.y) * scale.y) + (Yoffset) - cameraOffsetScaleAdjY
        ]);
        const pos = worldPosInPixels
        const relativeX = (pos.x / Resolution.Width) * 100;
        const relativeY = (pos.y / Resolution.Height) * 100;
        //return [50 - relativeX, 50 + relativeY, 100, -relativeY + 15];
        return [50 - relativeX, 50 + relativeY];
    }

    tutorialWantsClicks() {
        return this.tutorialGraph.active !== null && this.tutorialGraph.active.requiresClick;
    }

    //Events
    onClick() {
        if (this.tutorialGraph.active && this.tutorialGraph.active.requiresClick) {
            this.nextStep(true);
            this.doStep()
        }
    }

    onSkipToGameplay() {
        this.tutorialGraph.active = null; //Skip to endpoint
        this.currentStep = null;
        this.doStep();
        this.gameClient.updateUI();
    }

    onShopEnter() {
        this.doEvent("shopEnter");
    }

    onRecapEnter() {
        this.doEvent("recapEnter");
    }

    onRecapVirusGrowthNotice() {
        this.doEvent("virusGrowthNotice");
    }

    onPreviewBuyCell(type: GameElement) {
        this.doEvent("cellPreview");
    }


    onBuyCell(cell: CompositionElement) {
        const player = this.gameClient.getPlayer()!;
        const existingCells = player.immuneCells.length

        if (existingCells > 1
            && this.gameClient.stats.cellsCombined === 0
        ) {
            const sameTypeCells = player.immuneCells.filter(c => cell !== c && c.type === cell.type);
            if (sameTypeCells.length > 0) {
                this.combineTutorialCells.push(sameTypeCells[0], cell);
                this.doEvent("boughtCombinable");
            }
        }

        this.doEvent("cellPurchase");
    }

    onRotateCell(cell: CompositionElement) {
        this.doEvent("cellRotation");
    }

    //TODO: Triggers every time?
    onCombineCell(cell: CompositionElement) {
        const cellsCombined = this.gameClient.stats.cellsCombined;
        if (cellsCombined === 1) {
            this.combineTutorialCells = [cell];
            this.doEvent("combineCell");
        }
    }

    onRewardCell() {
        if (this.gameClient.stats.cellsTripled < 2) {
            this.doEvent("rewardCell");
        }
    }

    onLevelUp() {
        this.doEvent("levelUp");
    }

    onBuyExperience() {
        this.doEvent("buyExperience");
    }

    onMoveCamera = (isPan: boolean, isZoom: boolean, cameraCenterPos: vec2) => {
        if (isPan) {
            this.doEvent("cameraPan");
        }
        else if (isZoom) {
            this.doEvent("cameraZoom");
        }
    }

    onTieBreaker() {
        this.doEvent("tiebreaker");
    }

    onInspectCell(element: GameElement) {
        if (element > GameElement.SHOPABLE_ELEMENT_NUMBER && element < GameElement.ENEMY_ELEMENT_NUMBER
        ) {
            this.doEvent("inspectInfected");
        }
    }
    onPopulationLimitReached() {
        this.doEvent("populationLimitReached");
    }
}